import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons/faExclamationTriangle';

import { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Button } from 'ui-components/components/button';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { ProgressBar } from 'ui-components/components/progress-bar';
import { Text, TextType } from 'ui-components/components/text';

import { messages } from './messages';
import styles from './styles.module.less';
import { SummaryErrorRow } from './summary-error-row/summary-error-row';
import { useQuery } from '../../../../../../hooks/routing/use-query';
import Global from '../../../../../../store/global-selectors';
import { WorkflowSteps } from '../../../../../../tracking/use-workflow-tracking';
import { useGetImportSummary } from '../../../../data/hooks/use-get-import-summary';
import { useCsvImportProgress } from '../../../../data/hooks/use-import-progress';
import { useOnImportFinished } from '../../../../data/hooks/use-on-import-finished';
import { ImportStatus } from '../../../../data/types/import-status';
import { csvJobIdParam } from '../../../constants';
import { useImportContext } from '../../import-context';
import { useStepDetectiveContext } from '../../step-detective/step-detective-context';
import { StepHeader } from '../../step-header/step-header';

/**
 * Basic component for generating body for import is processing step.
 * Don't add functions to it here unless every type of import will support it.
 * Instead, create your own component and assign it to the renderBody in your step.
 * @param {object} props - Props passed to the component.
 * @param {(string|React.ReactNode)} props.title - Step Title.
 * @param {(string|React.ReactNode)} props.successMessage - Primary success message displayed after import completes successfully.
 * @param {object} [props.detailedSuccessMessage] - Optional detailed success message displayed below the primary success message when there are no warnings. Falls back to default message if not provided.
 * @returns {React.JSX.Element}
 */
export const ImportIsProcessingStepBody = ({ title, successMessage, detailedSuccessMessage }) => {
  const { importType, trackProcess, onComplete } = useImportContext();
  const { markActiveStepAsDone } = useStepDetectiveContext();

  const latestImport = useSelector((state) => Global.selectCsvImportType(state, importType));

  const { data, hasError, fetchSummary, isGetSummaryLoading } = useGetImportSummary();

  const fetchLatestImportSummary = useCallback(
    () =>
      fetchSummary(
        latestImport.jobId,
        () =>
          trackProcess(WorkflowSteps.CSV_IMPORT_GET_SUMMARY, {
            jobId: latestImport.jobId,
          }),
        (error) => trackProcess(WorkflowSteps.CSV_IMPORT_GET_SUMMARY_ERROR, { error }),
      ),
    [fetchSummary, latestImport.jobId, trackProcess],
  );

  const fetchSummaryWithJobId = useCallback(
    (jobId) =>
      fetchSummary(
        jobId,
        () => trackProcess(WorkflowSteps.CSV_IMPORT_GET_SUMMARY, { jobId }),
        (error) => trackProcess(WorkflowSteps.CSV_IMPORT_GET_SUMMARY_ERROR, { error }),
      ),
    [fetchSummary, trackProcess],
  );

  useOnImportFinished(
    importType,
    useCallback(() => {
      fetchLatestImportSummary();
      markActiveStepAsDone();
      onComplete();
    }, [fetchLatestImportSummary, markActiveStepAsDone, onComplete]),
  );

  const importJobIdFromToast = useQuery().get(csvJobIdParam);
  useEffect(() => {
    if (importJobIdFromToast && latestImport.status !== ImportStatus.ACTIVE) {
      fetchSummaryWithJobId(importJobIdFromToast);
    }
  }, [fetchSummaryWithJobId, importJobIdFromToast, latestImport.status]);

  const progress = useCsvImportProgress(importType);

  if (hasError) {
    return (
      <div className={styles['step-wrapper']}>
        <StepHeader>{title}</StepHeader>
        <>
          <Text textType={TextType.Description} value={messages.errorLoadingSummary} />
          <Button className={styles['result-error-btn']} onClick={fetchLatestImportSummary} theme='primary'>
            <FormattedMessage {...messages.retryBtn} />
          </Button>
        </>
      </div>
    );
  }

  return (
    <div className={styles['step-wrapper']}>
      <StepHeader>{title}</StepHeader>
      {latestImport.status === ImportStatus.ACTIVE && (
        <div>
          <div className={styles['active-import']}>
            <Text className={styles['active-import-title']} textType={TextType.H4} value={messages.importInProgress} />
            <Text
              className={styles['active-import-progress']}
              formattedMessageValues={{
                processed: progress?.processed,
                total: progress?.totalRecords,
              }}
              textType={TextType.Paragraph}
              value={progress ? messages.progressDetails : messages.checkingProgress}
            />
            <ProgressBar className={styles['progress-bar']} percentageComplete={progress?.percentageComplete} theme='primary' />
          </div>
          <BannerMessageV2 content={<Text textType={TextType.Paragraph} value={messages.closeInfoMessage} />} status={BannerStatus.Info} />
        </div>
      )}
      {latestImport.status === ImportStatus.FAILED && <Text textType={TextType.Description} value={messages.unexpectedFail} />}
      {latestImport.status === ImportStatus.COMPLETE && (
        <>
          {isGetSummaryLoading ? (
            <div className={styles['import-success']}>
              <LoadingIndicator className={styles['loading-indicator']} data-testid='summary-is-loading-step-indicator' />
              <Text
                className={styles['import-success-message']}
                textType={TextType.Description}
                value={messages.loadingSummaryInProgress}
              />
            </div>
          ) : null}
          {!isGetSummaryLoading && (
            <>
              {data?.failedCount !== data?.totalRecords && (
                <div className={styles['import-success']}>
                  <FontAwesomeIcon className={styles['icon-success']} icon={faCheckCircle} />
                  <Text className={styles['import-success-message']} textType={TextType.Description} value={successMessage} />
                  {data?.warningsCount ? (
                    <Text
                      className={styles['import-success-details']}
                      formattedMessageValues={{
                        warningsCount: data.warningsCount,
                        processed: data.processed,
                      }}
                      textType={TextType.Paragraph}
                      value={messages.importDetailsWarnings}
                    />
                  ) : (
                    <Text
                      className={styles['import-success-details']}
                      formattedMessageValues={{ processed: data.processed }}
                      textType={TextType.Paragraph}
                      value={detailedSuccessMessage ?? messages.importDetailsSuccess}
                    />
                  )}
                </div>
              )}
              {data?.errors?.length > 0 && (
                <div>
                  {data?.failedCount !== 0 && (
                    <div className={styles['error-header']}>
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                      <Text
                        className={styles['error-header-message']}
                        formattedMessageValues={{
                          failedCount: data.failedCount,
                        }}
                        textType={TextType.Paragraph}
                        value={messages.failedImportedRows}
                      />
                    </div>
                  )}
                  <Text className={styles['error-header-summary']} textType={TextType.Paragraph} value={messages.importSummaryErrors} />
                  {data.errors.map((error, index) => {
                    return (
                      <div key={index} className={styles['error-message']}>
                        <SummaryErrorRow error={error} />
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

ImportIsProcessingStepBody.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  successMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
};

export const ImportIsProcessingStepFooter = () => {
  const { onClose } = useImportContext();

  return (
    <Button className={styles['close-btn']} onClick={onClose} theme='primary'>
      <Text textType={TextType.Caption} value={messages.closeBtn} />
    </Button>
  );
};
